import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { FileText, Plus, Download } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentVault = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('business');
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`, {
        headers: getAuthHeaders()
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDocument = async () => {
    if (!title.trim() || !prompt.trim()) return;

    setGenerating(true);
    try {
      await axios.post(
        `${API}/documents`,
        { title, type: docType, prompt },
        { headers: getAuthHeaders() }
      );
      toast.success('Document generated successfully');
      setShowCreate(false);
      setTitle('');
      setPrompt('');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="text-muted-foreground mt-4">Loading documents...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="document-vault-page">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-5xl font-bold text-white mb-3">DOCUMENT VAULT</h1>
            <p className="text-muted-foreground text-base sm:text-lg">Professional document generation</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="new-document-button"
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-5 py-3 sm:px-8 sm:py-4 flex items-center gap-2 shrink-0"
          >
            <Plus className="w-4 h-4" />
            Generate Document
          </button>
        </div>

        {/* Document Types */}
        <div className="glass-card p-8 border border-white/10">
          <h2 className="text-2xl font-bold text-white mb-6 uppercase tracking-tight">Document Types</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {['Legal', 'Financial', 'Business', 'Contract', 'Report'].map((type) => (
              <div key={type} className="bg-secondary/30 p-6 border border-border text-center">
                <p className="text-sm font-medium uppercase tracking-wider text-white">{type}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Documents */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6 uppercase tracking-tight">Your Documents</h2>
          
          {documents.length === 0 ? (
            <div className="glass-card p-12 border border-white/10 text-center">
              <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
              <h3 className="text-xl font-bold text-white mb-3">No Documents Yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Generate professional legal, financial, and business documents with AI
              </p>
              <button
                onClick={() => setShowCreate(true)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-8 py-4"
              >
                Generate First Document
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {documents.map((doc) => (
                <div key={doc.id} className="glass-card p-8 border border-white/10 hover:border-primary/50 transition-colors" data-testid={`document-${doc.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        <h3 className="text-xl font-bold text-white">{doc.title}</h3>
                        <span className="text-xs uppercase tracking-widest text-muted-foreground px-3 py-1 bg-secondary/50">
                          {doc.type}
                        </span>
                      </div>
                      <p className="text-muted-foreground text-sm mb-4">
                        Created {new Date(doc.created_at).toLocaleDateString()}
                      </p>
                      <div className="bg-secondary/30 p-6 border border-border max-h-48 overflow-y-auto scrollbar-hide">
                        <pre className="text-sm text-white whitespace-pre-wrap font-sans">
                          {doc.content.substring(0, 500)}...
                        </pre>
                      </div>
                    </div>
                    <button
                      data-testid={`download-document-${doc.id}`}
                      className="ml-6 p-3 bg-secondary hover:bg-secondary/80 transition-colors"
                    >
                      <Download className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Generate Document Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => !generating && setShowCreate(false)}>
            <div className="glass-card p-8 border border-white/10 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()} data-testid="generate-document-modal">
              <h3 className="text-2xl font-bold text-white mb-6 uppercase tracking-tight">Generate Document</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-3">Document Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Investment Agreement"
                    disabled={generating}
                    data-testid="document-title-input"
                    className="w-full bg-secondary/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-6 py-4 text-white disabled:opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-3">Document Type</label>
                  <select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    disabled={generating}
                    data-testid="document-type-select"
                    className="w-full bg-secondary/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-6 py-4 text-white disabled:opacity-50"
                  >
                    <option value="legal">Legal</option>
                    <option value="financial">Financial</option>
                    <option value="business">Business</option>
                    <option value="contract">Contract</option>
                    <option value="report">Report</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-3">Instructions</label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe what you need in this document..."
                    rows="6"
                    disabled={generating}
                    data-testid="document-prompt-input"
                    className="w-full bg-secondary/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-6 py-4 text-white resize-none disabled:opacity-50"
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={handleGenerateDocument}
                    disabled={generating || !title.trim() || !prompt.trim()}
                    data-testid="confirm-generate-document"
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-8 py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generating ? 'GENERATING...' : 'GENERATE'}
                  </button>
                  <button
                    onClick={() => setShowCreate(false)}
                    disabled={generating}
                    data-testid="cancel-generate-document"
                    className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/80 uppercase tracking-widest text-xs font-bold px-8 py-4 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DocumentVault;
